import { addDaysYMD, todayISO } from "./lib/date";

export type DemoProfileId = "cut_ahead" | "bulk_lean" | "maintain";

export type DemoProfile = {
  id: DemoProfileId;
  label: string;
  vitals: any;
  entries: any[];
  sprints: any[];
  forecast: any;
  roadmap: any | null;
};

const DAY = 24 * 60 * 60 * 1000;
const REFERENCE_TODAY = "2026-02-25";

function ymdShift(ymd: string, days: number) {
  return addDaysYMD(ymd, days) ?? ymd;
}

function isoNowOffset(days = 0) {
  return new Date(Date.now() + days * DAY).toISOString();
}

function dayDiff(aYmd: string, bYmd: string) {
  const a = new Date(`${aYmd}T00:00:00Z`).getTime();
  const b = new Date(`${bYmd}T00:00:00Z`).getTime();
  return Math.round((a - b) / DAY);
}

function makeEntry(date: string, weight: number, caloriesIn: number | null, totalBurned: number | null, tdeeSource: string) {
  const netCalories = caloriesIn !== null && totalBurned !== null ? caloriesIn - totalBurned : null;
  return {
    date,
    weight: Number(weight.toFixed(1)),
    caloriesIn,
    totalBurned,
    activeCalories: null,
    netCalories,
    bmr: null,
    tdeeSource,
    theoreticalWeight: Number(weight.toFixed(1)),
    updatedAt: isoNowOffset(-1),
  };
}

function buildWeightOnlyTimeline({
  startDate,
  days,
  startWeight,
  endWeight,
  wave = 0.12,
  skipEvery = 0,
}: {
  startDate: string;
  days: number;
  startWeight: number;
  endWeight: number;
  wave?: number;
  skipEvery?: number;
}) {
  const rows: any[] = [];
  const dailySlope = days > 0 ? (endWeight - startWeight) / days : 0;
  for (let i = 0; i <= days; i += 1) {
    if (skipEvery > 0 && i > 20 && i % skipEvery === 0) continue;
    const date = ymdShift(startDate, i);
    const noise = Math.sin(i / 4.3) * wave + Math.cos(i / 9.7) * (wave * 0.5);
    const blend = i === 0 || i === days ? 0 : noise;
    const weight = startWeight + dailySlope * i + blend;
    rows.push(makeEntry(date, weight, null, null, "demo"));
  }
  return rows;
}

function buildActiveSprintTimeline({
  startDate,
  elapsedDays,
  startWeight,
  currentWeight,
  caloriesInBase,
  totalBurnedBase,
  caloriesWave = 140,
  tdeeWave = 125,
  weightWave = 0.1,
}: {
  startDate: string;
  elapsedDays: number;
  startWeight: number;
  currentWeight: number;
  caloriesInBase: number;
  totalBurnedBase: number;
  caloriesWave?: number;
  tdeeWave?: number;
  weightWave?: number;
}) {
  const rows: any[] = [];
  const dailySlope = elapsedDays > 0 ? (currentWeight - startWeight) / elapsedDays : 0;

  for (let i = 0; i <= elapsedDays; i += 1) {
    const date = ymdShift(startDate, i);
    const calNoise = Math.sin(i / 3.2) * caloriesWave + Math.cos(i / 8.1) * (caloriesWave * 0.45);
    const burnNoise = Math.cos(i / 3.9) * tdeeWave + Math.sin(i / 7.3) * (tdeeWave * 0.4);
    const caloriesIn = Math.round(caloriesInBase + calNoise);
    const totalBurned = Math.round(totalBurnedBase + burnNoise);
    const weightNoise = i === 0 || i === elapsedDays ? 0 : Math.sin(i / 4.6) * weightWave + Math.cos(i / 10.2) * (weightWave * 0.5);
    const weight = startWeight + dailySlope * i + weightNoise;
    rows.push(makeEntry(date, weight, caloriesIn, totalBurned, "demo"));
  }

  return rows;
}

type HistorySegment = {
  id: string;
  name: string;
  type: "baseline" | "bulk" | "cut" | "maintain";
  startDate: string;
  endDate: string;
  startWeight: number;
  endWeight: number;
  color: string;
  phaseId: string;
  phaseOrderIndex: number;
};

function buildUnifiedHistory(todayYmd: string) {
  const shiftDays = dayDiff(todayYmd, REFERENCE_TODAY);
  const shift = (d: string) => ymdShift(d, shiftDays);

  const segments: HistorySegment[] = [
    { id: "h-1", name: "Baseline (Early 2023)", type: "baseline", startDate: shift("2023-03-08"), endDate: shift("2023-11-01"), startWeight: 175.0, endWeight: 176.0, color: "#0f766e", phaseId: "hist-1", phaseOrderIndex: 0 },
    { id: "h-2", name: "Strong Bulk", type: "bulk", startDate: shift("2023-11-08"), endDate: shift("2024-04-03"), startWeight: 176.0, endWeight: 187.0, color: "#f59e0b", phaseId: "hist-2", phaseOrderIndex: 1 },
    { id: "h-3", name: "Post-Bulk Cut", type: "cut", startDate: shift("2024-04-10"), endDate: shift("2024-07-03"), startWeight: 187.0, endWeight: 167.5, color: "#3b82f6", phaseId: "hist-3", phaseOrderIndex: 2 },
    { id: "h-4", name: "Keeping it Lean", type: "maintain", startDate: shift("2024-07-10"), endDate: shift("2024-10-30"), startWeight: 167.5, endWeight: 167.5, color: "#8b5cf6", phaseId: "hist-4", phaseOrderIndex: 3 },
    { id: "h-5", name: "Baseline (Late 2024)", type: "baseline", startDate: shift("2024-11-06"), endDate: shift("2025-05-14"), startWeight: 167.5, endWeight: 172.0, color: "#0f766e", phaseId: "hist-5", phaseOrderIndex: 4 },
    { id: "h-6", name: "Trimming the Fat", type: "cut", startDate: shift("2025-05-21"), endDate: shift("2025-08-13"), startWeight: 172.0, endWeight: 160.0, color: "#3b82f6", phaseId: "hist-6", phaseOrderIndex: 5 },
    { id: "h-7", name: "Return to Demo Start", type: "baseline", startDate: shift("2025-08-20"), endDate: shift("2026-01-21"), startWeight: 160.0, endWeight: 170.0, color: "#0f766e", phaseId: "hist-7", phaseOrderIndex: 6 },
  ];

  const entries = segments.flatMap((s) => {
    const days = Math.max(1, dayDiff(s.endDate, s.startDate));
    return buildWeightOnlyTimeline({
      startDate: s.startDate,
      days,
      startWeight: s.startWeight,
      endWeight: s.endWeight,
      wave: s.type === "maintain" ? 0.08 : 0.12,
      skipEvery: 0,
    });
  });

  const sprintSegments = segments.filter((s) => ["bulk", "cut", "maintain"].includes(s.type));

  return {
    entries,
    sprintSegments,
    allSegments: segments,
    demoStartDate: segments[segments.length - 1].endDate,
    demoStartWeight: 170,
  };
}

function buildRoadmap({
  id,
  activeType,
  activeStart,
  activeTarget,
  activeStartWeight,
  activeEndWeight,
  activeSprintId,
}: {
  id: string;
  activeType: "cut" | "bulk" | "maintain";
  activeStart: string;
  activeTarget: string;
  activeStartWeight: number;
  activeEndWeight: number;
  activeSprintId: string;
}) {
  const maintenanceStart = ymdShift(activeTarget, 1);
  const maintenanceEnd = ymdShift(maintenanceStart, 56);

  return {
    id,
    name: "Demo Plan",
    createdAt: ymdShift(activeStart, -2),
    phases: [
      {
        id: "active-phase",
        orderIndex: 0,
        type: activeType,
        startDate: activeStart,
        endDate: activeTarget,
        startWeight: activeStartWeight,
        endWeight: activeEndWeight,
      },
      {
        id: "next-maintenance",
        orderIndex: 1,
        type: "maintain",
        startDate: maintenanceStart,
        endDate: maintenanceEnd,
        startWeight: activeEndWeight,
        endWeight: activeEndWeight,
      },
    ],
    phaseSprintLinks: {
      "active-phase": activeSprintId,
    },
  };
}

function buildCutAheadProfile(todayYmd: string): DemoProfile {
  const history = buildUnifiedHistory(todayYmd);
  const activeStart = ymdShift(todayYmd, -30);
  const activeTarget = ymdShift(activeStart, 60);
  const elapsedDays = 30;
  const planToday = 170 + ((160 - 170) / 60) * elapsedDays;
  const currentWeight = planToday - 2.0;

  const activeEntries = buildActiveSprintTimeline({
    startDate: activeStart,
    elapsedDays,
    startWeight: 170,
    currentWeight,
    caloriesInBase: 2125,
    totalBurnedBase: 2940,
    caloriesWave: 135,
    tdeeWave: 120,
    weightWave: 0.12,
  });

  const entries = [...history.entries, ...activeEntries].sort((a, b) => (a.date < b.date ? -1 : 1));

  return {
    id: "cut_ahead",
    label: "Cut (Ahead of schedule)",
    vitals: { height: 71, gender: "M", birthday: "1987-04-17", activityLevel: "moderate", activityFactor: 1.5 },
    entries,
    sprints: [
      {
        id: "cut-ahead-active",
        name: "Demo Cut Phase",
        startDate: activeStart,
        endDate: null,
        color: "#3b82f6",
        forecastEndDate: activeTarget,
        createdAt: ymdShift(activeStart, -2),
        updatedAt: isoNowOffset(-1),
        roadmapId: "roadmap-cut-ahead",
        phaseId: "active-phase",
        phaseOrderIndex: 0,
      },
      ...history.sprintSegments
        .map((s) => ({
          id: `hist-${s.id}`,
          name: s.name,
          startDate: s.startDate,
          endDate: s.endDate,
          color: s.color,
          forecastEndDate: s.endDate,
          createdAt: ymdShift(s.startDate, -1),
          updatedAt: isoNowOffset(-60),
          roadmapId: "roadmap-cut-ahead",
          phaseId: s.phaseId,
          phaseOrderIndex: s.phaseOrderIndex,
        }))
        .reverse(),
    ],
    forecast: { targetWeight: 160, targetDate: activeTarget, assumedNetCalories: -815, activityFactor: 1.5, inputMode: "net", sprintStartDate: activeStart, activeSprintId: "cut-ahead-active" },
    roadmap: buildRoadmap({
      id: "roadmap-cut-ahead",
      activeType: "cut",
      activeStart,
      activeTarget,
      activeStartWeight: 170,
      activeEndWeight: 160,
      activeSprintId: "cut-ahead-active",
    }),
  };
}

function buildLeanBulkProfile(todayYmd: string): DemoProfile {
  const history = buildUnifiedHistory(todayYmd);
  const activeStart = ymdShift(todayYmd, -35);
  const activeTarget = ymdShift(activeStart, 70);
  const elapsedDays = 35;
  const planToday = 170 + ((176 - 170) / 70) * elapsedDays;
  const currentWeight = planToday;

  const activeEntries = buildActiveSprintTimeline({
    startDate: activeStart,
    elapsedDays,
    startWeight: 170,
    currentWeight,
    caloriesInBase: 3010,
    totalBurnedBase: 2710,
    caloriesWave: 165,
    tdeeWave: 140,
    weightWave: 0.11,
  });

  return {
    id: "bulk_lean",
    label: "Lean Bulk",
    vitals: { height: 67, gender: "F", birthday: "1992-09-08", activityLevel: "very-active", activityFactor: 1.7 },
    entries: [...history.entries, ...activeEntries].sort((a, b) => (a.date < b.date ? -1 : 1)),
    sprints: [
      {
        id: "bulk-lean-active",
        name: "Demo Lean Bulk Phase",
        startDate: activeStart,
        endDate: null,
        color: "#f59e0b",
        forecastEndDate: activeTarget,
        createdAt: ymdShift(activeStart, -1),
        updatedAt: isoNowOffset(-1),
        roadmapId: "roadmap-bulk-lean",
        phaseId: "active-phase",
        phaseOrderIndex: 0,
      },
      ...history.sprintSegments
        .map((s) => ({
          id: `hist-${s.id}`,
          name: s.name,
          startDate: s.startDate,
          endDate: s.endDate,
          color: s.color,
          forecastEndDate: s.endDate,
          createdAt: ymdShift(s.startDate, -1),
          updatedAt: isoNowOffset(-90),
          roadmapId: "roadmap-bulk-lean",
          phaseId: s.phaseId,
          phaseOrderIndex: s.phaseOrderIndex,
        }))
        .reverse(),
    ],
    forecast: { targetWeight: 176, targetDate: activeTarget, assumedNetCalories: 300, activityFactor: 1.7, inputMode: "net", sprintStartDate: activeStart, activeSprintId: "bulk-lean-active" },
    roadmap: buildRoadmap({
      id: "roadmap-bulk-lean",
      activeType: "bulk",
      activeStart,
      activeTarget,
      activeStartWeight: 170,
      activeEndWeight: 176,
      activeSprintId: "bulk-lean-active",
    }),
  };
}

function buildMaintainProfile(todayYmd: string): DemoProfile {
  const history = buildUnifiedHistory(todayYmd);
  const activeStart = ymdShift(todayYmd, -32);
  const activeTarget = ymdShift(activeStart, 64);
  const elapsedDays = 32;
  const planToday = 170;
  const currentWeight = planToday - 0.5;

  const activeEntries = buildActiveSprintTimeline({
    startDate: activeStart,
    elapsedDays,
    startWeight: 170,
    currentWeight,
    caloriesInBase: 2580,
    totalBurnedBase: 2635,
    caloriesWave: 155,
    tdeeWave: 130,
    weightWave: 0.09,
  });

  return {
    id: "maintain",
    label: "Maintenance",
    vitals: { height: 69, gender: "M", birthday: "1984-01-20", activityLevel: "light", activityFactor: 1.3 },
    entries: [...history.entries, ...activeEntries].sort((a, b) => (a.date < b.date ? -1 : 1)),
    sprints: [
      {
        id: "maintain-active",
        name: "Demo Maintenance Phase",
        startDate: activeStart,
        endDate: null,
        color: "#8b5cf6",
        forecastEndDate: activeTarget,
        createdAt: ymdShift(activeStart, -1),
        updatedAt: isoNowOffset(-1),
        roadmapId: "roadmap-maintain",
        phaseId: "active-phase",
        phaseOrderIndex: 0,
      },
      ...history.sprintSegments
        .map((s) => ({
          id: `hist-${s.id}`,
          name: s.name,
          startDate: s.startDate,
          endDate: s.endDate,
          color: s.color,
          forecastEndDate: s.endDate,
          createdAt: ymdShift(s.startDate, -1),
          updatedAt: isoNowOffset(-90),
          roadmapId: "roadmap-maintain",
          phaseId: s.phaseId,
          phaseOrderIndex: s.phaseOrderIndex,
        }))
        .reverse(),
    ],
    forecast: { targetWeight: 170, targetDate: activeTarget, assumedNetCalories: -55, activityFactor: 1.3, inputMode: "net", sprintStartDate: activeStart, activeSprintId: "maintain-active" },
    roadmap: buildRoadmap({
      id: "roadmap-maintain",
      activeType: "maintain",
      activeStart,
      activeTarget,
      activeStartWeight: 170,
      activeEndWeight: 170,
      activeSprintId: "maintain-active",
    }),
  };
}

export function buildDemoProfiles(todayYmd = todayISO()): Record<DemoProfileId, DemoProfile> {
  return {
    cut_ahead: buildCutAheadProfile(todayYmd),
    bulk_lean: buildLeanBulkProfile(todayYmd),
    maintain: buildMaintainProfile(todayYmd),
  };
}
